import { RefObject, useEffect, useState } from 'react';

import { formatRefId } from 'root/apps/QuoteMachineIntegration/utils/referenceHelpers';
import { datadogRum } from 'root/datadog/datadogRum';

import {
  CUSTOMER_CREDIT_ACCOUNT_ELEMENT,
  CUSTOMER_INVOICES_LIST_ELEMENT,
  getCustomerCreditAccountScriptUrl,
  getCustomerInvoicesListScriptUrl,
} from './config';
import {
  CUSTOMER_CREDIT_ACCOUNT_PAY_BALANCE_MOUNT_READY_EVENT,
  CUSTOMER_CREDIT_ACCOUNT_PAY_BALANCE_MOUNT_REMOVED_EVENT,
  type CustomerCreditAccountPayBalanceMountLifecycleDetail,
  type PayBalanceMountHandlers,
} from './events';
import {
  createInvoicingActionHandler,
  createInvoicingErrorHandler,
  INVOICING_ACTION_EVENT,
  INVOICING_ERROR_EVENT,
  type InvoicingActionRouterContext,
} from './useInvoicingActionRouter';

import type {
  CustomerCreditAccountWebComponentConfig,
  CustomerInvoicesListWebComponentConfig,
  InvoicingCustomerCreditAccountElement,
  InvoicingCustomerInvoicesListElement,
} from './types';

function loadWebComponentScript(
  customElementTag: string,
  scriptUrl: string,
  setReady: (ready: boolean) => void
): (() => void) | undefined {
  let cancelled = false;
  const markReady = () => {
    if (!cancelled) setReady(true);
  };

  if (customElements.get(customElementTag)) {
    setReady(true);
    return undefined;
  }

  const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
  if (existingScript) {
    customElements.whenDefined(customElementTag).then(markReady);
    return () => {
      cancelled = true;
    };
  }

  const script = document.createElement('script');
  script.type = 'module';
  script.src = scriptUrl;
  script.onload = () => {
    customElements.whenDefined(customElementTag).then(markReady);
  };
  script.onerror = () => {
    datadogRum.addError(new Error(`Failed to load ${customElementTag} script`));
  };
  document.head.appendChild(script);

  return () => {
    cancelled = true;
    // Keep the shared script tag — another host may still be waiting on whenDefined().
  };
}

export function useCustomerCreditAccountWebComponent(
  containerRef: RefObject<HTMLElement>,
  config: CustomerCreditAccountWebComponentConfig | null,
  actionContext: InvoicingActionRouterContext | null,
  payBalanceHandlers?: PayBalanceMountHandlers | null
): void {
  const [isScriptReady, setIsScriptReady] = useState(false);

  useEffect(() => {
    return loadWebComponentScript(
      CUSTOMER_CREDIT_ACCOUNT_ELEMENT,
      getCustomerCreditAccountScriptUrl(),
      setIsScriptReady
    );
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isScriptReady || !config || !actionContext) return undefined;

    const el = document.createElement(
      CUSTOMER_CREDIT_ACCOUNT_ELEMENT
    ) as InvoicingCustomerCreditAccountElement;

    const handleAction = createInvoicingActionHandler(el, actionContext);
    const handleError = createInvoicingErrorHandler();
    const handlePayBalanceMountReady = (event: Event) => {
      const mountPoint = (event as CustomEvent<CustomerCreditAccountPayBalanceMountLifecycleDetail>)
        .detail?.mountPoint;
      if (mountPoint) {
        payBalanceHandlers?.mountPayBalance(mountPoint);
      }
    };
    const handlePayBalanceMountRemoved = (event: Event) => {
      const mountPoint = (event as CustomEvent<CustomerCreditAccountPayBalanceMountLifecycleDetail>)
        .detail?.mountPoint;
      if (mountPoint) {
        payBalanceHandlers?.unmountPayBalance(mountPoint);
      }
    };

    el.addEventListener(INVOICING_ACTION_EVENT, handleAction);
    el.addEventListener(INVOICING_ERROR_EVENT, handleError);
    if (payBalanceHandlers) {
      el.addEventListener(
        CUSTOMER_CREDIT_ACCOUNT_PAY_BALANCE_MOUNT_READY_EVENT,
        handlePayBalanceMountReady
      );
      el.addEventListener(
        CUSTOMER_CREDIT_ACCOUNT_PAY_BALANCE_MOUNT_REMOVED_EVENT,
        handlePayBalanceMountRemoved
      );
    }
    container.appendChild(el);

    el.init?.(config).catch(() => {
      // Errors are handled inside the web component.
    });

    return () => {
      el.removeEventListener(INVOICING_ACTION_EVENT, handleAction);
      el.removeEventListener(INVOICING_ERROR_EVENT, handleError);
      if (payBalanceHandlers) {
        el.removeEventListener(
          CUSTOMER_CREDIT_ACCOUNT_PAY_BALANCE_MOUNT_READY_EVENT,
          handlePayBalanceMountReady
        );
        el.removeEventListener(
          CUSTOMER_CREDIT_ACCOUNT_PAY_BALANCE_MOUNT_REMOVED_EVENT,
          handlePayBalanceMountRemoved
        );
      }
      if (el.parentNode === container) {
        container.removeChild(el);
      }
    };
  }, [actionContext, config, containerRef, isScriptReady, payBalanceHandlers]);
}

export function useCustomerInvoicesListWebComponent(
  containerRef: RefObject<HTMLElement>,
  config: CustomerInvoicesListWebComponentConfig | null
): void {
  const [isScriptReady, setIsScriptReady] = useState(false);

  useEffect(() => {
    return loadWebComponentScript(
      CUSTOMER_INVOICES_LIST_ELEMENT,
      getCustomerInvoicesListScriptUrl(),
      setIsScriptReady
    );
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isScriptReady || !config) return undefined;

    const el = document.createElement(
      CUSTOMER_INVOICES_LIST_ELEMENT
    ) as InvoicingCustomerInvoicesListElement;

    const handleAction = createInvoicingActionHandler(el, {
      accountID: config.accountId ?? '',
      customerID: config.customerId,
      creditAccountID: config.creditAccountId,
    });
    const handleError = createInvoicingErrorHandler();

    el.addEventListener(INVOICING_ACTION_EVENT, handleAction);
    el.addEventListener(INVOICING_ERROR_EVENT, handleError);
    container.appendChild(el);

    el.init?.(config).catch(() => {
      // Errors are handled inside the web component.
    });

    return () => {
      el.removeEventListener(INVOICING_ACTION_EVENT, handleAction);
      el.removeEventListener(INVOICING_ERROR_EVENT, handleError);
      if (el.parentNode === container) {
        container.removeChild(el);
      }
    };
  }, [config, containerRef, isScriptReady]);
}

export function buildCreditAccountRef(creditAccountId: string | number): string {
  return formatRefId(String(creditAccountId));
}
